import { Box, Divider, Popover, Slider, Switch, Typography } from '@mui/material';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { HeapMaskEntry } from '../../Heap/heapMasks';

interface HeapMaskSettingsPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  entries: readonly HeapMaskEntry[];
  onVisibilityChange: (maskId: string, visible: boolean) => void;
  onOpacityChange: (maskId: string, opacity: number) => void;
}

export function HeapMaskSettingsPopover({
  anchorEl,
  open,
  onClose,
  entries,
  onVisibilityChange,
  onOpacityChange,
}: HeapMaskSettingsPopoverProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { width: 300, padding: 1.5, borderRadius: 2 } } }}
    >
      <Typography variant='subtitle2' paddingX={0.5} paddingBottom={1}>
        {t('heap.masks.settings')}
      </Typography>

      {entries.map((entry, index) => (
        <Box key={entry.id}>
          {index > 0 && <Divider sx={{ marginY: 1 }} />}
          <HeapMaskSettingsRow
            entry={entry}
            onVisibilityChange={onVisibilityChange}
            onOpacityChange={onOpacityChange}
          />
        </Box>
      ))}
    </Popover>
  );
}

interface HeapMaskSettingsRowProps {
  entry: HeapMaskEntry;
  onVisibilityChange: (maskId: string, visible: boolean) => void;
  onOpacityChange: (maskId: string, opacity: number) => void;
}

function HeapMaskSettingsRow({ entry, onVisibilityChange, onOpacityChange }: HeapMaskSettingsRowProps): ReactElement {
  const { t } = useTranslation();
  const label = t(entry.labelKey);

  return (
    <Box paddingX={0.5}>
      <Box display='flex' alignItems='center' gap={1}>
        <Box
          component='span'
          width={12}
          height={12}
          borderRadius='2px'
          flexShrink={0}
          sx={{ backgroundColor: entry.color }}
          aria-hidden
        />
        <Typography variant='body2' flex={1} noWrap title={label}>
          {label}
        </Typography>
        <Switch
          size='small'
          checked={entry.enabled}
          onChange={(event) => onVisibilityChange(entry.id, event.target.checked)}
          inputProps={{ 'aria-label': label }}
        />
      </Box>

      <Box display='flex' alignItems='center' gap={1.5} paddingTop={0.5}>
        <Typography variant='caption' color='text.secondary' minWidth={56}>
          {t('heap.masks.opacity')}
        </Typography>
        <Slider
          size='small'
          min={0}
          max={100}
          step={5}
          value={Math.round(entry.opacity * 100)}
          onChange={(_, value) => onOpacityChange(entry.id, (value as number) / 100)}
          aria-label={`${label} – ${t('heap.masks.opacity')}`}
          sx={{ flex: 1, color: entry.color }}
        />
        <Typography variant='caption' color='text.secondary' minWidth={34} textAlign='right'>
          {`${Math.round(entry.opacity * 100)}%`}
        </Typography>
      </Box>
    </Box>
  );
}
